import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Progress } from '../ui/progress';
import { Handshake, Target, CheckCircle, XCircle, AlertTriangle } from 'lucide-react';
import { IntroductionAnalysis } from '../../services/analyticsApi';

interface IntroductionQualityCardProps {
  analysis: IntroductionAnalysis | undefined;
}

export const IntroductionQualityCard: React.FC<IntroductionQualityCardProps> = ({ analysis }) => {
  if (!analysis) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Handshake className="h-5 w-5 text-blue-600" />
            Introduction Quality
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-gray-500">No introduction data available</p>
        </CardContent>
      </Card>
    );
  }

  const getQualityColor = (score: number) => {
    if (score >= 0.8) return 'text-green-600';
    if (score >= 0.6) return 'text-yellow-600';
    return 'text-red-600';
  };

  const getQualityBadge = (score: number) => {
    if (score >= 0.8) return { label: 'Excellent', variant: 'default' as const };
    if (score >= 0.6) return { label: 'Good', variant: 'secondary' as const };
    return { label: 'Needs Improvement', variant: 'destructive' as const };
  };

  const qualityBadge = getQualityBadge(analysis.introduction_quality_score);

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg flex items-center gap-2">
          <Handshake className="h-5 w-5 text-blue-600" />
          Introduction Quality
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Overall Quality Score */}
        <div className="flex items-center justify-between p-3 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg border border-blue-100">
          <div>
            <p className="text-sm text-gray-600">Quality Score</p>
            <p className={`text-2xl font-bold ${getQualityColor(analysis.introduction_quality_score)}`}>
              {(analysis.introduction_quality_score * 100).toFixed(0)}%
            </p>
          </div>
          <Badge variant={qualityBadge.variant}>{qualityBadge.label}</Badge>
        </div>

        {/* Match Quality */}
        <div className="flex items-center justify-between p-2 bg-gray-50 rounded">
          <span className="text-sm text-gray-700">Match Quality</span>
          <Badge variant="outline" className="capitalize">
            {analysis.match_quality}
          </Badge>
        </div>

        {/* Mutual Value Assessment */}
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium text-gray-700">Mutual Value Detected</span>
            {analysis.mutual_value_detected ? (
              <CheckCircle className="h-5 w-5 text-green-600" />
            ) : (
              <XCircle className="h-5 w-5 text-red-600" />
            )}
          </div>
        </div>

        {/* Value Opportunities */}
        {analysis.specific_value_opportunities && analysis.specific_value_opportunities.length > 0 && (
          <div className="space-y-2">
            <h4 className="text-sm font-medium text-gray-700">Value Opportunities</h4>
            <ul className="space-y-1 pl-4 border-l-2 border-blue-200">
              {analysis.specific_value_opportunities.map((opportunity, idx) => (
                <li key={idx} className="text-sm text-gray-600">
                  {opportunity}
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Warnings */}
        {analysis.introduction_quality_score < 0.6 && (
          <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-lg flex items-start gap-2">
            <AlertTriangle className="h-4 w-4 text-yellow-600 flex-shrink-0 mt-0.5" />
            <div className="text-xs text-yellow-800">
              <p className="font-medium">Low Quality Introduction</p>
              <p className="mt-1">Consider re-introducing with more context about mutual value.</p>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

